/**
 * Item Create Media Context
 * 
 * Shared state for staged media (images) during item creation.
 * Used by both sidebar (DocSidebar draft mode) and main area (ItemCreateOverviewWidget)
 * to keep gallery state synchronized.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { normalizeFileUrl } from '@/lib/attachments/utils';

export interface StagedMediaItem {
  file_url: string;
  file_name: string;
  localPreviewUrl?: string; // Blob URL for instant preview
  is_primary_image?: boolean; // Whether this is the primary image
  idx?: number; // Display order (1-based)
}

interface ItemCreateMediaContextValue {
  stagedMedia: StagedMediaItem[];
  setStagedMedia: React.Dispatch<React.SetStateAction<StagedMediaItem[]>>;
  // Helper methods
  addMedia: (items: StagedMediaItem[]) => void;
  removeMedia: (file_url: string) => void;
  setPrimary: (file_url: string) => void;
  reorder: (newOrder: StagedMediaItem[]) => void;
  removeMany: (file_urls: string[]) => void;
}

const ItemCreateMediaContext = createContext<ItemCreateMediaContextValue | null>(null);

export function ItemCreateMediaProvider({ children }: { children: React.ReactNode }) {
  const [stagedMedia, setStagedMedia] = useState<StagedMediaItem[]>([]);
  
  // Track latest stagedMedia in ref for cleanup (avoids stale closure)
  const stagedMediaRef = useRef<StagedMediaItem[]>([]);
  useEffect(() => {
    stagedMediaRef.current = stagedMedia;
  }, [stagedMedia]);
  
  // Cleanup blob URLs on unmount (uses ref to access latest stagedMedia)
  useEffect(() => {
    return () => {
      stagedMediaRef.current.forEach((img) => {
        if (img.localPreviewUrl) {
          URL.revokeObjectURL(img.localPreviewUrl);
        }
      });
    };
  }, []); // Empty deps - only cleanup on unmount

  // Add media items (with deduplication)
  const addMedia = useCallback((items: StagedMediaItem[]) => {
    setStagedMedia((prev) => {
      const existingUrls = new Set(
        prev.map((img) => normalizeFileUrl(img.file_url) ?? img.file_url)
      );

      const newItems = items.filter((item) => {
        const normalized = normalizeFileUrl(item.file_url) ?? item.file_url;
        return !existingUrls.has(normalized);
      });

      if (newItems.length === 0) return prev;

      // Assign indices if not provided
      const maxIdx = prev.length > 0 ? Math.max(...prev.map((img) => img.idx || 0)) : 0;
      const itemsWithIdx = newItems.map((item, index) => ({
        ...item,
        idx: item.idx ?? maxIdx + index + 1,
      }));

      return [...prev, ...itemsWithIdx];
    });
  }, []);

  // Remove a single media item
  const removeMedia = useCallback((file_url: string) => {
    setStagedMedia((prev) => {
      const normalized = normalizeFileUrl(file_url) ?? file_url;

      const removed = prev.find((img) => {
        const imgNormalized = normalizeFileUrl(img.file_url) ?? img.file_url;
        return imgNormalized === normalized;
      });

      // Cleanup blob URL if exists
      if (removed?.localPreviewUrl) {
        URL.revokeObjectURL(removed.localPreviewUrl);
      }

      const updated = prev.filter((img) => {
        const imgNormalized = normalizeFileUrl(img.file_url) ?? img.file_url;
        return imgNormalized !== normalized;
      });

      // Reindex remaining items
      return updated.map((img, index) => ({
        ...img,
        idx: index + 1,
      }));
    });
  }, []);

  // Set primary image (only one can be primary)
  const setPrimary = useCallback((file_url: string) => {
    setStagedMedia((prev) => {
      const normalized = normalizeFileUrl(file_url) ?? file_url;

      return prev.map((img) => {
        const imgNormalized = normalizeFileUrl(img.file_url) ?? img.file_url;
        return {
          ...img,
          is_primary_image: imgNormalized === normalized,
        };
      });
    });
  }, []);

  // Reorder media items
  const reorder = useCallback((newOrder: StagedMediaItem[]) => {
    setStagedMedia(
      newOrder.map((img, index) => ({
        ...img,
        idx: index + 1,
      }))
    );
  }, []);

  // Remove multiple media items (for delete mode)
  const removeMany = useCallback((file_urls: string[]) => {
    setStagedMedia((prev) => {
      const normalizedUrls = new Set(
        file_urls.map((url) => normalizeFileUrl(url) ?? url)
      );

      // Cleanup blob URLs
      prev.forEach((img) => {
        const imgNormalized = normalizeFileUrl(img.file_url) ?? img.file_url;
        if (normalizedUrls.has(imgNormalized) && img.localPreviewUrl) {
          URL.revokeObjectURL(img.localPreviewUrl);
        }
      });

      const updated = prev.filter((img) => {
        const imgNormalized = normalizeFileUrl(img.file_url) ?? img.file_url;
        return !normalizedUrls.has(imgNormalized);
      });

      // Reindex remaining items
      return updated.map((img, index) => ({
        ...img,
        idx: index + 1,
      }));
    });
  }, []);

  const value = useMemo(
    () => ({
      stagedMedia,
      setStagedMedia,
      addMedia,
      removeMedia,
      setPrimary,
      reorder,
      removeMany,
    }),
    [stagedMedia, addMedia, removeMedia, setPrimary, reorder, removeMany]
  );

  return <ItemCreateMediaContext.Provider value={value}>{children}</ItemCreateMediaContext.Provider>;
}

export function useItemCreateMedia() {
  const context = useContext(ItemCreateMediaContext);
  if (!context) {
    throw new Error('useItemCreateMedia must be used within ItemCreateMediaProvider');
  }
  return context;
}

/**
 * Optional version of useItemCreateMedia that returns null instead of throwing.
 * Use this when the component needs to work both with and without the provider.
 * 
 * @returns Context value or null if provider is not present
 */
export function useOptionalItemCreateMedia(): ItemCreateMediaContextValue | null {
  return useContext(ItemCreateMediaContext);
}
